import { cn } from "@ui/lib";
import { ChevronDownIcon } from "lucide-react";

/**
 * Key-takeaways treatments for the AI-citation page template. Facts render
 * as real DOM text server-side (never behind a count-up or canvas): the
 * audience for this element includes AI crawlers, and a fact that only
 * exists after hydration isn't a fact to them.
 *
 * Two treatments; pick per page:
 * - StatStrip: visible band of value + label facts under the hero.
 * - CollapsedTakeaways: a `details` block for pages where the facts would
 *   crowd the design; skimmers can open it, crawlers read it either way.
 */

export function StatStrip({
	items,
	className,
}: {
	items: Array<{ value: string; label: string }>;
	className?: string;
}) {
	return (
		<dl
			className={cn(
				"grid gap-px overflow-hidden rounded-2xl border bg-border sm:grid-cols-2",
				items.length === 3 ? "lg:grid-cols-3" : "lg:grid-cols-4",
				className,
			)}
		>
			{items.map((item) => (
				<div key={item.label} className="flex flex-col bg-card px-5 py-4">
					<dt className="order-2 mt-1 text-xs text-muted-foreground">
						{item.label}
					</dt>
					<dd className="order-1 font-mono text-xl font-extrabold tracking-tight">
						{item.value}
					</dd>
				</div>
			))}
		</dl>
	);
}

export function CollapsedTakeaways({
	summary = "TL;DR",
	facts,
	className,
}: {
	summary?: string;
	facts: string[];
	className?: string;
}) {
	return (
		<details
			className={cn(
				"group rounded-xl border bg-muted/40 px-4 py-3",
				className,
			)}
		>
			<summary className="flex cursor-pointer list-none items-center gap-2 text-sm font-bold text-muted-foreground [&::-webkit-details-marker]:hidden">
				{summary}
				<ChevronDownIcon
					className="size-4 transition-transform group-open:rotate-180"
					aria-hidden
				/>
			</summary>
			<ul className="mt-3 space-y-1.5 text-sm text-muted-foreground">
				{facts.map((fact) => (
					<li key={fact} className="flex gap-2">
						<span aria-hidden>·</span>
						{fact}
					</li>
				))}
			</ul>
		</details>
	);
}
